import { useMemo } from 'react';

import { Reports, TotalityResponse } from '@/app/models/costTrackingTotal';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import TextButton from '@/components/common/TextButton';
import { TableCell, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface RubricTableHeaderProps {
  setDrawerEdit: CallableFunction;
  setSelectedHeaderItem: (id: Reports[]) => void;
  data: TotalityResponse | undefined;
}

export default function RubricTableHeader({ setDrawerEdit, setSelectedHeaderItem, data }: RubricTableHeaderProps) {
  const theme = useTheme();

  const { t } = useTranslation(['costTracking', 'common']);

  const handleDrowersEdit = () => {
    setDrawerEdit(true);
  };

  const columns: Column[] = [
    { key: 'baseline', label: t('baseline'), width: theme.spacing(30) },
    { key: 'paybook', label: t('total.forecasted'), width: theme.spacing(30) },
    { key: 'fulfilled', label: t('total.fulfilled'), width: theme.spacing(30) },
    { key: 'replanned', label: t('total.fulfilled.plus.forecasted') },
    { key: 'difference', label: t('difference') },
    { key: 'actions', label: t('common:actions'), align: 'center', width: theme.spacing(20) },
  ];

  const tableRows = useMemo(() => {
    if (!data?.reports || data.reports.length === 0) return [];
    return [data.reports];
  }, [data]);

  const renderRow = (item: Reports[]) => {
    const findReport = (type: string) => item.find((r) => r.planningReportType === type);

    const format = (type: string) => {
      const report = findReport(type);
      return report ? formatterMonetaryValue(report.value) : '-';
    };

    const replannedItem = findReport('REPLANNED');
    return (
      <>
        <TableCell sx={bodyCellSx}>{format('BASELINE')}</TableCell>
        <TableCell sx={bodyCellSx}>{format('REPLANNED')}</TableCell>
        <TableCell sx={bodyCellSx}>{format('FULFILLED')}</TableCell>
        <TableCell sx={bodyCellSx}>{format('PAYBOOK')}</TableCell>
        <TableCell sx={bodyCellSx}>{format('DIFFERENCE')}</TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          <TextButton
            sx={{ ml: 'auto' }}
            text={`+ ${t('common:comment')}`}
            onClick={() => {
              if (replannedItem) {
                handleDrowersEdit();
                setSelectedHeaderItem(item);
              }
            }}
          />
        </TableCell>
      </>
    );
  };

  const getRowKey = (reports: Reports[]) => reports[0]?.id || 'summary';

  return (
    <DataTable
      columns={columns}
      data={tableRows}
      renderRow={renderRow}
      getRowKey={getRowKey}
      emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
      size="small"
    />
  );
}
